/**
 * The name of a country, from its ISO 3166-1 alpha-2 code (backend #682 decision 3).
 *
 * Plain functions rather than a pipe, for the same reason as `vat-label`: the only consumers are
 * Kendo dropdowns, whose `textField` reads a property off the item.
 *
 * Countries are the other closed set the platform deliberately does not translate: the backend
 * ships `code2` and the clients label it. `Intl.DisplayNames` is the browser's own CLDR data, so
 * „Deutschland" / „Germany" costs nothing to maintain and covers every country at once — where a
 * translation table would have to be filled in by hand and would go stale.
 *
 * The backend's `CountryModel.name` is German prose. It is what the dropdowns rendered until now,
 * which is why an English page listed „Deutschland", „Frankreich", „Vereinigtes Königreich".
 */
/** Intl.DisplayNames for a locale, or null where the runtime does not provide it. */
export function createDisplayNames(localeId: string): Intl.DisplayNames | null {
  try {
    // `fallback: 'none'` makes an unknown code answer `undefined` instead of echoing itself
    // back, which is what lets the backend's own name be preferred over a non-label.
    return new Intl.DisplayNames([localeId], { type: 'region', fallback: 'none' });
  } catch {
    return null;
  }
}

export function countryName(
  display: Intl.DisplayNames | null,
  code2: string | null | undefined,
  fallback?: string | null,
): string {
  const code = code2?.trim().toUpperCase();
  // Intl wants exactly two letters; anything else throws rather than returning undefined. `ZZ`
  // is CLDR's own "unknown region", and „Unbekannte Region" says less than whatever the backend
  // chose to call that row.
  if (!display || !code || !/^[A-Z]{2}$/.test(code) || code === 'ZZ') {
    return fallback ?? code2 ?? '';
  }
  try {
    // `of` echoes the code back when it knows no name for it — that is not a label.
    const name = display.of(code);
    return !name || name === code ? (fallback ?? code) : name;
  } catch {
    return fallback ?? code;
  }
}

/** A country as a dropdown item: the id stays the value, the label becomes the localized name. */
export interface CountryItem {
  id: string;
  name: string;
}

/**
 * The country list as the dropdowns need it (#178).
 *
 * Kendo's `textField` reads a property, so the localized name has to exist as one. Sorted by the
 * label with the active locale's collator — „Österreich" belongs next to „Oldenburg" in German
 * and the backend's own order is by its German name anyway, which is wrong under `/en/`.
 */
export function toCountryItems(
  countries: ReadonlyArray<{ id: string; code2?: string; name?: string }> | null | undefined,
  localeId: string,
): CountryItem[] {
  const display = createDisplayNames(localeId);
  return (countries ?? [])
    .map((c) => ({ id: c.id, name: countryName(display, c.code2, c.name) }))
    .sort((a, b) => a.name.localeCompare(b.name, localeId));
}
